import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, CheckCircle2, Search, Eye, Monitor, Folder } from "lucide-react";

export interface SoftwareItem {
  id: number;
  category: string;
  title: string;
  description: string;
  tags: string[];
  url: string;
  detailsUrl?: string;
  status: string;
  iconUrl: string;
  highlightStat: { label: string; value: string };
  features: string[];
}

export const softwareData: SoftwareItem[] = [
  {
    id: 1,
    category: "crm",
    title: "CRMPro By Hickter Complete Crm solution",
    description: "CRMPro By Hickter is an all-in-one customer relationship management platform designed to automate lead capture, client communication, pipeline management, invoicing, and team workflows.",
    tags: ["PHP", "MySQL", "React", "TailwindCSS"],
    url: "https://crmdemo.hickter.com/admin/authentication",
    detailsUrl: "/ourproducts/app/crmpro",
    status: "Live Production CRM",
    iconUrl: "/assets/logo/crmpro.png",
    highlightStat: { label: "Deployment", value: "Cloud / On-Premise" },
    features: ["Automated Lead Scoring", "Project & Milestone Tracker", "Multi-Currency Invoicing", "Real-Time Team Analytics"]
  },
  {
    id: 2,
    category: "pos",
    title: "POS Inventory Woocommerce Synced By Hickter",
    description: "Cloud-native Point of Sale system seamlessly synchronized with WooCommerce e-commerce stores for real-time inventory management, stock tracking, and multi-channel checkout.",
    tags: ["WooCommerce", "WordPress", "React", "Node.js", "Custom API"],
    url: "https://posdemo.hickter.com/",
    detailsUrl: "/ourproducts/app/pos-woocommerce",
    status: "Live POS Engine",
    iconUrl: "https://ik.imagekit.io/filennky/Products%20Hickter/posapp.webp?updatedAt=1784371788310",
    highlightStat: { label: "Sync Engine", value: "WooCommerce Auto-Sync" },
    features: ["Real-Time Inventory Sync", "Hardware Printer & Scanner API", "Multi-Store Checkout", "Customer Sales Ledger"]
  },
  {
    id: 3,
    category: "tracking",
    title: "Tracknn By Hickter Notes, reminder, activity tracking",
    description: "Tracknn By Hickter is a smart productivity and activity tracking platform featuring instant notes, task reminders, habit schedules, and team workflow monitoring.",
    tags: ["React", "Node.js", "MongoDB", "TailwindCSS"],
    url: "https://tracknn.hickter.com",
    detailsUrl: "/ourproducts/app/tracknn",
    status: "Production Cloud App",
    iconUrl: "https://ik.imagekit.io/filennky/Products%20Hickter/tracknn.png?updatedAt=1784371494392",
    highlightStat: { label: "Productivity", value: "Instant Sync & Alerts" },
    features: ["Instant Smart Notes", "Automated Task Reminders", "Activity Log Tracker", "Team Collaboration Board"]
  }
];

const CATEGORIES = [
  { id: 1, label: "All Software", slug: "all" },
  { id: 2, label: "CRM Solution", slug: "crm" },
  { id: 3, label: "POS Inventory", slug: "pos" },
  { id: 4, label: "Activity Tracking", slug: "tracking" }
];

export default function SoftwareProducts({ items = softwareData }: { items?: SoftwareItem[] }) {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCards = useMemo(() => {
    return items.filter((item) => {
      const matchesCategory = activeTab === "all" || item.category === activeTab;
      const matchesSearch =
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesCategory && matchesSearch;
    });
  }, [activeTab, searchQuery, items]);

  const handleReset = () => {
    setActiveTab("all");
    setSearchQuery("");
  };

  return (
    <div className="space-y-6 font-sans">
      {/* FILTER & SEARCH CONTROL BAR */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.slug)}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeTab === cat.slug
                  ? "bg-blue-600 dark:bg-blue-500 !text-white dark:!text-white shadow-md shadow-blue-500/20 font-extrabold"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 font-bold"
              }`}
            >
              <Folder size={14} className={activeTab === cat.slug ? "!text-white dark:!text-white" : ""} />
              <span className={activeTab === cat.slug ? "!text-white dark:!text-white font-extrabold" : ""}>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input
            type="text"
            placeholder="Search software products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs font-medium pl-9 pr-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 focus:border-blue-500 outline-none transition-colors"
          />
        </div>
      </div>

      {/* SOFTWARE PRODUCTS GRID */}
      {filteredCards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCards.map((item) => (
            <div
              key={item.id}
              className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-md hover:shadow-xl hover:border-blue-500/50 dark:hover:border-blue-500/50 transition-all flex flex-col justify-between space-y-5"
            >
              <div className="space-y-4">
                {/* Header Badge & Status */}
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-2 flex items-center justify-center shadow-xs">
                    <img
                      src={item.iconUrl}
                      alt={item.title}
                      className="w-full h-full object-contain rounded-lg"
                      onError={(e: any) => {
                        e.target.onerror = null;
                        e.target.src = "https://ik.imagekit.io/filennky/Products%20Hickter/crmsuite.png?updatedAt=1784371494392";
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-500/20">
                    ● {item.status}
                  </span>
                </div>

                {/* Title & Description */}
                <div className="space-y-1.5">
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed font-medium">
                    {item.description}
                  </p>
                </div>

                {/* Highlight Stat */}
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                    {item.highlightStat.label}
                  </span>
                  <span className="text-xs font-black text-blue-600 dark:text-blue-400">
                    {item.highlightStat.value}
                  </span>
                </div>

                {/* Features List */}
                <div className="space-y-1.5 pt-1">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 block">
                    Core Capabilities:
                  </span>
                  <div className="grid grid-cols-1 gap-1">
                    {item.features.map((feat, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={13} className="text-blue-600 dark:text-blue-400 shrink-0" />
                        <span className="truncate">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {item.tags.map((tag, tIdx) => (
                    <span key={tIdx} className="text-[10px] font-bold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-2.5 py-0.5 rounded border border-blue-200 dark:border-blue-500/20">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2">
                {item.detailsUrl && (
                  <Link
                    to={item.detailsUrl}
                    className="btn-outline flex-1 py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5"
                  >
                    <Eye size={14} />
                    <span>View Details</span>
                  </Link>
                )}
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary-blue flex-1 py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 !text-white shadow-md shadow-blue-500/20"
                >
                  <Monitor size={14} className="!text-white" />
                  <span className="!text-white">Web Demo</span>
                  <ExternalLink size={12} className="!text-white" />
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
          <p className="text-slate-600 dark:text-slate-400 text-sm font-semibold">No software products match your filter parameters.</p>
          <button onClick={handleReset} className="btn-outline text-xs px-4 py-2 rounded-xl">
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
}
